import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit, computed, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { Subject, catchError, forkJoin, of, takeUntil } from "rxjs";
import { LearningPath, ProblemCategory, ProblemListItem } from "../../models/problem.models";
import { BookmarkService } from "../../services/bookmark.service";
import { GameService } from "../../services/game.service";
import { ProblemService } from "../../services/problem.service";

type FilterStatus = "All" | "Solved" | "Unsolved";
type SortBy = "recommended" | "popular" | "newest";

@Component({
  selector: "app-problem-library",
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: "./problem-library.component.html",
  styleUrl: "./problem-library.component.css"
})
export class ProblemLibraryComponent implements OnInit, OnDestroy {
  loading = true;
  error = "";
  loadingProblems = false;
  bookmarkBusy = signal<number[]>([]);

  readonly problems = signal<ProblemListItem[]>([]);
  readonly totalCount = signal(0);
  readonly categories = signal<ProblemCategory[]>([]);
  readonly learningPaths = signal<LearningPath[]>([]);
  readonly dailyChallenge = signal<{
    id: number;
    title: string;
    world: string;
    difficulty: string;
    levelNumber: number;
    xpReward: number;
  } | null>(null);

  readonly search = signal("");
  readonly difficulty = signal("All");
  readonly topic = signal("All");
  readonly status = signal<FilterStatus>("All");
  readonly sort = signal<SortBy>("recommended");
  readonly page = signal(1);
  readonly pageSize = 10;

  readonly topicOptions = computed(() => ["All", ...this.categories().map((item) => item.topic)]);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.pageSize)));
  readonly paged = computed(() => this.problems());
  readonly hasFilters = computed(
    () =>
      this.search().trim().length > 0 ||
      this.difficulty() !== "All" ||
      this.topic() !== "All" ||
      this.status() !== "All" ||
      this.sort() !== "recommended"
  );

  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly problemService: ProblemService,
    private readonly gameService: GameService,
    private readonly bookmarkService: BookmarkService
  ) {}

  ngOnInit(): void {
    this.loadInitial();
  }

  ngOnDestroy(): void {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string) {
    this.search.set(value);
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => {
      this.resetPagination();
      this.loadProblems();
    }, 250);
  }

  onDifficultyChange(value: string) {
    this.difficulty.set(value || "All");
    this.resetPagination();
    this.loadProblems();
  }

  onTopicChange(value: string) {
    this.topic.set(value || "All");
    this.resetPagination();
    this.loadProblems();
  }

  onStatusChange(value: FilterStatus) {
    this.status.set(value || "All");
    this.resetPagination();
    this.loadProblems();
  }

  onSortChange(value: SortBy) {
    this.sort.set(value || "recommended");
    this.resetPagination();
    this.loadProblems();
  }

  applyTopic(topic: string) {
    this.topic.set(topic);
    this.resetPagination();
    this.loadProblems();
  }

  clearFilters() {
    this.search.set("");
    this.difficulty.set("All");
    this.topic.set("All");
    this.status.set("All");
    this.sort.set("recommended");
    this.resetPagination();
    this.loadProblems();
  }

  toggleBookmark(item: ProblemListItem) {
    if (this.isBookmarkBusy(item.id)) {
      return;
    }
    this.setBookmarkBusy(item.id, true);
    const finalizeToggle = (didSucceed: boolean) => {
      if (didSucceed) {
        this.problems.update((rows) =>
          rows.map((row) => (row.id === item.id ? { ...row, isBookmarked: !item.isBookmarked } : row))
        );
      }
      this.setBookmarkBusy(item.id, false);
    };

    if (item.isBookmarked) {
      this.bookmarkService
        .removeByProblem(item.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => finalizeToggle(true),
          error: () => finalizeToggle(false)
        });
      return;
    }

    this.bookmarkService
      .add(item.id, "Practice List")
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => finalizeToggle(true),
        error: () => finalizeToggle(false)
      });
  }

  prevPage() {
    this.goToPage(this.page() - 1);
  }

  nextPage() {
    this.goToPage(this.page() + 1);
  }

  goToPage(page: number) {
    const total = this.totalPages();
    const next = Math.min(Math.max(1, page), total);
    this.page.set(next);
    this.loadProblems();
  }

  pageStart() {
    if (this.totalCount() === 0) {
      return 0;
    }
    return (this.page() - 1) * this.pageSize + 1;
  }

  pageEnd() {
    return Math.min(this.page() * this.pageSize, this.totalCount());
  }

  trackByTopicOption(_: number, option: string) {
    return option;
  }

  trackByCategory(_: number, category: ProblemCategory) {
    return category.topic;
  }

  trackByProblem(_: number, item: ProblemListItem) {
    return item.id;
  }

  trackByPath(_: number, path: LearningPath) {
    return path.id;
  }

  trackByNextProblem(_: number, item: { id: number }) {
    return item.id;
  }

  private loadInitial() {
    this.loading = true;
    this.error = "";

    forkJoin({
      categories: this.problemService.getCategories().pipe(catchError(() => of([] as ProblemCategory[]))),
      learningPaths: this.problemService.getLearningPaths().pipe(catchError(() => of([] as LearningPath[]))),
      dailyChallenge: this.gameService.getDailyChallenge().pipe(catchError(() => of(null)))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.categories.set(result.categories);
          this.learningPaths.set(result.learningPaths);
          this.dailyChallenge.set(result.dailyChallenge);
          this.loadProblems(() => {
            this.loading = false;
          });
        },
        error: (error) => {
          this.error = error?.error?.message || "Failed to load problem library";
          this.loading = false;
        }
      });
  }

  private loadProblems(onDone?: () => void) {
    this.loadingProblems = true;
    const solved =
      this.status() === "Solved" ? "solved" : this.status() === "Unsolved" ? "unsolved" : ("all" as const);

    this.problemService
      .getProblemLibrary({
        difficulty: this.difficulty(),
        topic: this.topic(),
        solved,
        search: this.search(),
        sort: this.sort(),
        page: this.page(),
        pageSize: this.pageSize
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.problems.set(response.items);
          this.totalCount.set(response.total);
          this.loadingProblems = false;
          onDone?.();
        },
        error: (error) => {
          this.error = error?.error?.message || "Failed to load problems";
          this.problems.set([]);
          this.totalCount.set(0);
          this.loadingProblems = false;
          onDone?.();
        }
      });
  }

  private resetPagination() {
    this.page.set(1);
  }

  private isBookmarkBusy(problemId: number) {
    return this.bookmarkBusy().includes(problemId);
  }

  private setBookmarkBusy(problemId: number, value: boolean) {
    this.bookmarkBusy.update((list) => {
      if (value) {
        if (list.includes(problemId)) {
          return list;
        }
        return [...list, problemId];
      }
      return list.filter((id) => id !== problemId);
    });
  }
}
